import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  CupSoda,
  FileText,
  Hand,
  Image as ImageIcon,
  Info,
  Layers,
  PackageOpen,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";
import {
  OBSTACLE_GAP_COLOR,
  OBSTACLE_MUTED_COLOR,
  isKnownObstacleClass,
  isObstacleNok,
  obstacleColor,
  obstaclePath,
  sortedObstacleClasses,
  type ObstacleCheck,
  type ObstacleClass,
  type ObstacleFrame,
  type ObstacleViewMode,
} from "@/lib/obstacles";

/**
 * Obstacle layer (backend photo_quality_evaluation.checks.obstacle_in_pizza, see pizza-backend
 * vision.obstacle_seg): the masks of what the segmentation found over the photo. The part of a mask that lies
 * on the pizza is hatched (that share is what the verdict counts), the rest is only tinted; an object beside
 * the pizza is grey. A failed crust gap (the check's second signal) is an arc on the rim.
 *
 * All geometry is in source-image pixels; the SVG viewBox is the same frame the photo canvas shows.
 */

const rad = (deg: number) => (deg * Math.PI) / 180;
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

/** crust gaps below this share of the rim are not drawn unless they fail the check */
const GAP_HINT_MIN_RATIO = 0.03;
/** share of the frame's height at its top and bottom left to the photo's buttons: no labels there */
const LABEL_SAFE_BAND = 0.1;
/** key of the crust gap among the hoverable items (the classes use their name) */
export const OBSTACLE_GAP_KEY = "crust_gap";

const CLASS_ICONS: Record<string, typeof Hand> = { hand: Hand, paper: FileText, lid: PackageOpen, cup: CupSoda };

function useClassLabel() {
  const { t } = useLocalization();
  return (name: string) => (isKnownObstacleClass(name) ? t(`obstacle.class.${name}` as TranslationKey) : name);
}

/**
 * Highlighting an item (a class or the crust gap): hover with a mouse; on a touch screen, where nothing
 * hovers, a tap selects it and a second tap lets go.
 */
function useHighlight(hovered: string | null, onHover: (key: string | null) => void) {
  const lastPointer = useRef("mouse");
  return (key: string) => ({
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType === "mouse") onHover(key);
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (e.pointerType === "mouse") onHover(null);
    },
    onPointerDown: (e: React.PointerEvent) => {
      lastPointer.current = e.pointerType;
    },
    onClick: () => {
      if (lastPointer.current !== "mouse") onHover(hovered === key ? null : key);
    },
  });
}

/** Number that runs up to `target` together with the scan pass. */
function useCountUp(target: number, animate: boolean, duration = 1050, delay = 350) {
  const [value, setValue] = useState(animate ? 0 : target);
  useEffect(() => {
    if (!animate) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now() + delay;
    const tick = (now: number) => {
      const k = clamp((now - start) / duration, 0, 1);
      setValue(target * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, animate, duration, delay]);
  return value;
}

/** Hatch of the part that covers the pizza: tinted base + diagonal stripes drifting slowly. */
function CoverHatch({ id, color, spacing, drift }: { id: string; color: string; spacing: number; drift: boolean }) {
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width={spacing} height={spacing} patternTransform="rotate(-45)">
      <rect width={spacing} height={spacing} fill={color} fillOpacity={0.3} />
      <g>
        <rect x={0} y={0} width={spacing * 0.34} height={spacing} fill={color} fillOpacity={0.88} />
        <rect x={spacing} y={0} width={spacing * 0.34} height={spacing} fill={color} fillOpacity={0.88} />
        {drift && (
          <animateTransform
            attributeName="transform"
            type="translate"
            from="0 0"
            to={`${-spacing} 0`}
            dur="2.4s"
            repeatCount="indefinite"
          />
        )}
      </g>
    </pattern>
  );
}

export function ObstacleOverlay({
  check,
  frame,
  hovered,
  onHover,
  mode = "mask",
  className,
}: {
  check: ObstacleCheck;
  frame: ObstacleFrame;
  /** hovered class name or OBSTACLE_GAP_KEY (shared with the panel under the photo) */
  hovered: string | null;
  onHover: (key: string | null) => void;
  /** mask = tinted, hatched masks and labels; source = the bare photo with a thin dashed outline */
  mode?: ObstacleViewMode;
  className?: string;
}) {
  const { t, formatNumber } = useLocalization();
  const classLabel = useClassLabel();
  const uid = useId().replace(/:/g, "");
  const reduced = usePrefersReducedMotion();
  const highlight = useHighlight(hovered, onHover);
  const details = check.details ?? {};
  const classes = useMemo(() => sortedObstacleClasses(details.segmentation), [details.segmentation]);
  const paths = useMemo(() => classes.map((c) => obstaclePath(c.polygons)), [classes]);
  const showMask = mode === "mask";
  const nok = isObstacleNok(check);

  // screen px per source px (the SVGs are letterboxed in the container like the photo canvas)
  const rootRef = useRef<HTMLDivElement>(null);
  const [displayScale, setDisplayScale] = useState<number | null>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const update = () => setDisplayScale(Math.min(el.clientWidth / frame.width, el.clientHeight / frame.height) || null);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [frame.width, frame.height]);
  const onScreen = (px: number) => (displayScale ? px / displayScale : 0);

  const circle = details.circle ?? null;
  // sizes follow the pizza, like the slicing layer, with a floor in screen px (a phone shows the photo small)
  const R = circle?.r ?? Math.min(frame.width, frame.height) * 0.45;
  const stroke = Math.max(2, R * 0.009, onScreen(1.6));
  const hatch = Math.max(8, R * 0.05, onScreen(7));
  const fontSize = Math.max(10, R * 0.05, onScreen(11));
  const dash = stroke * 3.2;
  const viewBox = `${frame.x} ${frame.y} ${frame.width} ${frame.height}`;
  const circumference = 2 * Math.PI * R;
  const pct = (v: number) => formatNumber(v * 100, { maximumFractionDigits: 1, minimumFractionDigits: 1 });

  const gapFailed = (details.detected_by ?? []).includes(OBSTACLE_GAP_KEY);
  const gapRatio = details.max_gap_ratio ?? 0;
  const gap = circle && details.max_gap && (gapFailed || gapRatio >= GAP_HINT_MIN_RATIO) ? details.max_gap : null;
  const rim = (deg: number, k = 1) =>
    circle ? { x: circle.x + k * R * Math.cos(rad(deg)), y: circle.y + k * R * Math.sin(rad(deg)) } : { x: 0, y: 0 };
  const gapPath = gap
    ? (() => {
        const p0 = rim(gap.start_deg);
        const p1 = rim(gap.start_deg + gap.size_deg);
        return `M ${p0.x} ${p0.y} A ${R} ${R} 0 ${gap.size_deg > 180 ? 1 : 0} 1 ${p1.x} ${p1.y}`;
      })()
    : null;

  const svgClass = "absolute inset-0 h-full w-full";
  const dimmed = (key: string) => hovered !== null && hovered !== key;

  // Label chips: the text decides the width. They stay inside the frame, clear of the buttons in the photo's
  // top and bottom corners, and step aside (towards the middle) when two would overlap.
  const labels = (() => {
    const placed: { x: number; y: number; w: number; h: number }[] = [];
    const h = fontSize * 1.7;
    const top = frame.y + frame.height * LABEL_SAFE_BAND + h / 2;
    const bottom = frame.y + frame.height * (1 - LABEL_SAFE_BAND) - h / 2;
    const place = (text: string, at: { x: number; y: number }, hasIcon: boolean) => {
      const w = fontSize * (0.58 * text.length + (hasIcon ? 2.3 : 1.4));
      const x = clamp(at.x, frame.x + w / 2 + stroke * 2, frame.x + frame.width - w / 2 - stroke * 2);
      let y = clamp(at.y, top, bottom);
      const step = (y > frame.y + frame.height / 2 ? -1 : 1) * h * 1.2;
      const overlaps = () =>
        placed.some((b) => Math.abs(b.x - x) < (b.w + w) / 2 + stroke && Math.abs(b.y - y) < (b.h + h) / 2 + stroke);
      for (let i = 0; i < 8 && overlaps(); i++) y = clamp(y + step, top, bottom);
      const box = { x, y, w, h };
      placed.push(box);
      return box;
    };
    const out: {
      key: string;
      text: string;
      color: string;
      icon: typeof Hand | null;
      anchor: { x: number; y: number } | null;
      box: { x: number; y: number; w: number; h: number };
    }[] = [];
    classes.forEach((c) => {
      if (!c.anchor) return;
      const covers = c.coverage > 0;
      const text = covers
        ? t("obstacle.labelCovers", { name: classLabel(c.name), value: pct(c.coverage) })
        : `${classLabel(c.name)} · ${t("obstacle.outside")}`;
      const at = { x: c.anchor[0], y: c.anchor[1] };
      out.push({
        key: c.name,
        text,
        color: covers ? obstacleColor(c.name) : OBSTACLE_MUTED_COLOR,
        icon: CLASS_ICONS[c.name] ?? Layers,
        anchor: covers ? at : null,
        box: place(text, at, true),
      });
    });
    if (gap && gapFailed) {
      const text = t("obstacle.gapLabel", { value: pct(gapRatio) });
      out.push({
        key: OBSTACLE_GAP_KEY,
        text,
        color: OBSTACLE_GAP_COLOR,
        icon: null,
        anchor: null,
        box: place(text, rim(gap.start_deg + gap.size_deg / 2, 0.8), false),
      });
    }
    return out;
  })();

  return (
    <div ref={rootRef} className={cn("obstacle-motion pointer-events-none absolute inset-0 overflow-hidden", className)}>
      {/* focus on the pizza: veil outside it, the rim, the crust gap */}
      <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" className={svgClass} aria-hidden>
        <defs>
          {circle && (
            <mask id={`${uid}-outside`}>
              <rect x={frame.x} y={frame.y} width={frame.width} height={frame.height} fill="white" />
              <circle cx={circle.x} cy={circle.y} r={R} fill="black" />
            </mask>
          )}
          <filter id={`${uid}-gap-glow`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation={stroke * 1.4} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g className="transition-opacity duration-500 ease-out" style={{ opacity: showMask ? 1 : 0 }}>
          {circle && (
            <>
              <rect
                x={frame.x}
                y={frame.y}
                width={frame.width}
                height={frame.height}
                fill="#0b0f19"
                mask={`url(#${uid}-outside)`}
                className="obstacle-veil"
              />
              <circle
                cx={circle.x}
                cy={circle.y}
                r={R}
                fill="none"
                stroke="white"
                strokeOpacity={0.75}
                strokeWidth={stroke * 0.7}
                strokeDasharray={circumference}
                strokeDashoffset={circumference}
                transform={`rotate(-90 ${circle.x} ${circle.y})`}
                className="obstacle-rim"
                style={{ ["--obstacle-len" as string]: `${circumference}` }}
              />
            </>
          )}
          {gapPath && (
            <g
              className="transition-opacity duration-300"
              style={{ opacity: dimmed(OBSTACLE_GAP_KEY) ? 0.3 : 1 }}
            >
              <path
                d={gapPath}
                fill="none"
                stroke={OBSTACLE_GAP_COLOR}
                strokeWidth={gapFailed ? stroke * 3.4 : stroke * 2}
                strokeLinecap="round"
                strokeDasharray={gapFailed ? undefined : `${stroke * 3} ${stroke * 3}`}
                filter={gapFailed ? `url(#${uid}-gap-glow)` : undefined}
                className="obstacle-gap"
              />
            </g>
          )}
        </g>
      </svg>

      {/* the masks, uncovered top-down under the scan beam */}
      <div
        className={cn("obstacle-reveal absolute inset-0 transition-opacity duration-500 ease-out")}
        style={{ opacity: showMask ? 1 : 0 }}
        aria-hidden={!showMask}
      >
        <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" className={svgClass} aria-label={t("obstacle.tab")}>
          <defs>
            {classes.map((c) => (
              <CoverHatch
                key={c.name}
                id={`${uid}-hatch-${c.name}`}
                color={obstacleColor(c.name)}
                spacing={hatch}
                drift={!reduced}
              />
            ))}
            {circle && (
              <clipPath id={`${uid}-pizza`}>
                <circle cx={circle.x} cy={circle.y} r={R} />
              </clipPath>
            )}
            <filter id={`${uid}-glow`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation={stroke * 2.2} />
            </filter>
          </defs>
          {classes.map((c, i) => {
            const d = paths[i];
            if (!d) return null;
            const covers = c.coverage > 0;
            const color = covers ? obstacleColor(c.name) : OBSTACLE_MUTED_COLOR;
            const isHovered = hovered === c.name;
            return (
              <g
                key={c.name}
                className="transition-opacity duration-300"
                style={{ opacity: dimmed(c.name) ? 0.28 : 1 }}
              >
                {/* soft halo behind the outline */}
                {covers && (
                  <path
                    d={d}
                    fill="none"
                    stroke={color}
                    strokeWidth={stroke * 3.2}
                    strokeLinejoin="round"
                    filter={`url(#${uid}-glow)`}
                    className="obstacle-glow"
                    style={{ opacity: 0.5 }}
                  />
                )}
                {/* the whole mask, tinted */}
                <path
                  d={d}
                  fillRule="evenodd"
                  fill={color}
                  fillOpacity={covers ? 0.2 : 0.16}
                  className={cn(showMask && "pointer-events-auto cursor-pointer")}
                  {...highlight(c.name)}
                />
                {/* the part on the pizza: this share decides the verdict */}
                {covers && (
                  <g clipPath={circle ? `url(#${uid}-pizza)` : undefined} style={{ pointerEvents: "none" }}>
                    <path d={d} fillRule="evenodd" fill={`url(#${uid}-hatch-${c.name})`} />
                  </g>
                )}
                <path
                  d={d}
                  fill="none"
                  stroke={color}
                  strokeWidth={stroke * (isHovered ? 1.9 : 1.5)}
                  strokeLinejoin="round"
                  strokeDasharray={covers ? undefined : `${dash} ${dash}`}
                  className="transition-[stroke-width] duration-300"
                />
                <path
                  d={d}
                  fill="none"
                  stroke="white"
                  strokeOpacity={isHovered ? 1 : 0.9}
                  strokeWidth={stroke * 0.55}
                  strokeLinejoin="round"
                  strokeDasharray={isHovered ? `${dash} ${dash}` : undefined}
                  className={cn(isHovered && !reduced && "obstacle-ants")}
                  style={{ ["--obstacle-dash" as string]: `${dash}px` }}
                />
              </g>
            );
          })}
        </svg>
      </div>

      {/* one pass per mount (it ends invisible): switching to the photo and back does not replay it */}
      {!reduced && <div className="obstacle-scan" />}

      {/* labels over the masks; in the source view only a thin dashed outline stays */}
      <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" className={svgClass} aria-hidden>
        <defs>
          <filter id={`${uid}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy={fontSize * 0.1} stdDeviation={fontSize * 0.16} floodOpacity="0.45" />
          </filter>
          <clipPath id={`${uid}-frame`}>
            <rect x={frame.x} y={frame.y} width={frame.width} height={frame.height} />
          </clipPath>
          <filter id={`${uid}-alert-glow`} x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation={stroke * 6} />
          </filter>
        </defs>
        {/* verdict frame: a red glow inside the photo's edge once the scan is done */}
        {nok && (
          <g className="transition-opacity duration-500 ease-out" style={{ opacity: showMask ? 1 : 0 }}>
            <g clipPath={`url(#${uid}-frame)`} className="obstacle-alert" style={{ opacity: 0.55 }}>
              <rect
                x={frame.x}
                y={frame.y}
                width={frame.width}
                height={frame.height}
                fill="none"
                stroke="#f43f5e"
                strokeOpacity={0.7}
                strokeWidth={stroke * 12}
                filter={`url(#${uid}-alert-glow)`}
              />
              <rect
                x={frame.x}
                y={frame.y}
                width={frame.width}
                height={frame.height}
                fill="none"
                stroke="#f43f5e"
                strokeWidth={stroke * 2.4}
              />
            </g>
          </g>
        )}
        <g className="transition-opacity duration-500 ease-out" style={{ opacity: showMask ? 0 : 1 }}>
          {classes.map((c, i) =>
            paths[i] ? (
              <g key={`source-${c.name}`}>
                <path d={paths[i]} fill="none" stroke="#0f172a" strokeOpacity={0.55} strokeWidth={stroke * 1.3} />
                <path
                  d={paths[i]}
                  fill="none"
                  stroke="white"
                  strokeWidth={stroke * 0.6}
                  strokeDasharray={`${dash} ${dash}`}
                />
              </g>
            ) : null,
          )}
        </g>
        <g className="transition-opacity duration-500 ease-out" style={{ opacity: showMask ? 1 : 0 }}>
          {labels.map(({ key, text, color, icon: Icon, anchor, box }, i) => {
            const isHovered = hovered === key;
            return (
              <g key={`label-${key}`} className="transition-opacity duration-300" style={{ opacity: dimmed(key) ? 0.35 : 1 }}>
                {anchor && !reduced && (
                  <g transform={`translate(${anchor.x} ${anchor.y})`}>
                    <circle r={stroke * 2.6} fill="none" stroke="white" strokeWidth={stroke * 0.6} className="obstacle-ripple" />
                  </g>
                )}
                <g transform={`translate(${box.x} ${box.y})`}>
                  <g className="obstacle-label" style={{ animationDelay: `${1250 + i * 110}ms` }}>
                    <g
                      className="transition-transform duration-300"
                      style={{ transform: `scale(${isHovered ? 1.12 : 1})` }}
                      filter={`url(#${uid}-shadow)`}
                    >
                      <rect
                        x={-box.w / 2}
                        y={-box.h / 2}
                        width={box.w}
                        height={box.h}
                        rx={box.h / 2}
                        fill="#0b1220"
                        fillOpacity={0.86}
                        stroke={color}
                        strokeWidth={fontSize * 0.1}
                      />
                      {Icon && (
                        <>
                          <circle cx={-box.w / 2 + box.h / 2} cy={0} r={box.h * 0.36} fill={color} />
                          <Icon
                            x={-box.w / 2 + box.h / 2 - fontSize * 0.42}
                            y={-fontSize * 0.42}
                            width={fontSize * 0.84}
                            height={fontSize * 0.84}
                            color="white"
                            strokeWidth={2.4}
                          />
                        </>
                      )}
                      <text
                        x={Icon ? -box.w / 2 + box.h * 1.02 : 0}
                        textAnchor={Icon ? "start" : "middle"}
                        dominantBaseline="central"
                        fill="white"
                        fontSize={fontSize}
                        fontWeight={700}
                        style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.01em" }}
                      >
                        {text}
                      </text>
                    </g>
                  </g>
                </g>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

/** what the check says, in one line */
function useVerdict(check: ObstacleCheck) {
  const { t } = useLocalization();
  const details = check.details ?? {};
  const coverage = details.segmentation?.coverage ?? null;
  if (isObstacleNok(check)) {
    return (details.detected_by ?? []).includes("segmentation") ? t("obstacle.verdictCovered") : t("obstacle.verdictGap");
  }
  return coverage !== null && coverage > 0 ? t("obstacle.verdictBelow") : t("obstacle.verdictClear");
}

/** Compact chip in the photo's bottom-left corner: the covered share runs up with the scan. */
export function ObstacleBadge({ check, className }: { check: ObstacleCheck; className?: string }) {
  const { t, formatNumber } = useLocalization();
  const reduced = usePrefersReducedMotion();
  const details = check.details ?? {};
  const segmentation = details.segmentation;
  const nok = isObstacleNok(check);
  const coverage = segmentation?.coverage ?? null;
  const shown = useCountUp(coverage ?? 0, !reduced);
  const pct = (v: number, digits = 1) =>
    formatNumber(v * 100, { maximumFractionDigits: digits, minimumFractionDigits: digits });
  const gapOnly = nok && !(details.detected_by ?? []).includes("segmentation");
  const Icon = nok ? ShieldAlert : ShieldCheck;

  return (
    <div
      className={cn(
        "obstacle-legend pointer-events-none absolute bottom-3 left-3 z-30 flex items-center gap-2 rounded-full",
        "border border-white/15 bg-slate-950/70 py-1 pl-1 pr-2.5 text-xs text-white shadow-xl backdrop-blur-md",
        className,
      )}
    >
      <span
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-full",
          nok ? "bg-rose-600" : "bg-emerald-600",
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      {gapOnly ? (
        <span className="font-medium tabular-nums">
          {t("obstacle.gapLabel", { value: pct(details.max_gap_ratio ?? 0) })}
        </span>
      ) : coverage === null ? (
        <span className="font-medium">{t("obstacle.skippedShort")}</span>
      ) : (
        <>
          <span className="font-medium tabular-nums">{t("obstacle.covered", { value: pct(shown) })}</span>
          <span
            className={cn(
              "rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
              nok ? "bg-rose-600 text-white" : "bg-emerald-600 text-white",
            )}
          >
            {t("obstacle.limit", { value: pct(segmentation?.threshold ?? 0, 0) })}
          </span>
        </>
      )}
    </div>
  );
}

function Swatch({ color, hatched }: { color: string; hatched: boolean }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0 overflow-visible">
      <defs>
        <pattern id={id} patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(-45)">
          <rect width="4" height="4" fill={color} fillOpacity={0.25} />
          <rect width="1.5" height="4" fill={color} />
        </pattern>
      </defs>
      <rect
        x="0.5"
        y="0.5"
        width="15"
        height="15"
        rx="4"
        fill={hatched ? `url(#${id})` : color}
        fillOpacity={hatched ? 1 : 0.2}
        stroke={color}
        strokeWidth="1"
        strokeDasharray={hatched ? undefined : "2 2"}
      />
    </svg>
  );
}

/** Share bar against a limit: the fill grows with the scan, the tick marks the limit. */
function LimitBar({
  value,
  limit,
  scale,
  color,
  className,
}: {
  value: number;
  limit: number;
  /** the value at the bar's right end */
  scale: number;
  color: string;
  className?: string;
}) {
  const x = (v: number) => `${clamp(v / scale, 0, 1) * 100}%`;
  return (
    <div className={cn("relative h-2 w-full rounded-full bg-muted", className)}>
      <div className="absolute inset-y-0 left-0 overflow-hidden rounded-full" style={{ width: x(value) }}>
        <div className="obstacle-bar h-full w-full rounded-full" style={{ background: color }} />
      </div>
      <div className="absolute -top-1 h-4 w-0.5 rounded bg-foreground/70" style={{ left: `calc(${x(limit)} - 1px)` }} />
    </div>
  );
}

/** Panel under the photo: the covered share against the limit, and what the check found. */
export function ObstaclePanel({
  check,
  preview,
  hovered,
  onHover,
}: {
  check: ObstacleCheck;
  /** computed on demand, not the stored evaluation */
  preview?: boolean;
  hovered: string | null;
  onHover: (key: string | null) => void;
}) {
  const { t, formatNumber } = useLocalization();
  const classLabel = useClassLabel();
  const reduced = usePrefersReducedMotion();
  const highlight = useHighlight(hovered, onHover);
  const verdict = useVerdict(check);
  const details = check.details ?? {};
  const segmentation = details.segmentation;
  const classes = useMemo(() => sortedObstacleClasses(segmentation), [segmentation]);
  const nok = isObstacleNok(check);
  const coverage = segmentation?.coverage ?? null;
  const threshold = segmentation?.threshold ?? 0;
  const shown = useCountUp(coverage ?? 0, !reduced);
  const pct = (v: number, digits = 1) =>
    formatNumber(v * 100, { maximumFractionDigits: digits, minimumFractionDigits: digits });
  // the limit is a few percent: the bars end where the picture is readable, not at 100 %
  const scale = Math.max(0.2, threshold * 3, (coverage ?? 0) * 1.15);

  const gapRatio = details.max_gap_ratio ?? 0;
  const gapThreshold = details.threshold_ratio ?? 0;
  const gapFailed = (details.detected_by ?? []).includes(OBSTACLE_GAP_KEY);
  const showGap = gapFailed || gapRatio >= GAP_HINT_MIN_RATIO;
  const Icon = nok ? ShieldAlert : ShieldCheck;
  const accent = nok ? "text-rose-600 dark:text-rose-400" : "text-emerald-700 dark:text-emerald-400";

  return (
    <div className="obstacle-legend grid gap-4 border-t px-4 pt-4 md:grid-cols-[15rem_1fr]">
      <div className="flex flex-col">
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-lg ring-1",
              nok ? "bg-rose-500/10 ring-rose-500/30" : "bg-emerald-500/10 ring-emerald-500/30",
            )}
          >
            <Icon className={cn("h-4 w-4", accent)} />
          </span>
          <div>
            <div className="text-sm font-semibold leading-tight">{t("obstacle.title")}</div>
            <div className={cn("text-xs font-medium", accent)}>{verdict}</div>
          </div>
        </div>

        {coverage !== null ? (
          <div className="mt-3">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">{t("obstacle.coverageTitle")}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {t("obstacle.limit", { value: pct(threshold, 0) })}
              </span>
            </div>
            {/* the number answers to its own limit: a photo rejected for the crust gap alone stays green here */}
            <div
              className={cn(
                "mt-0.5 text-3xl font-semibold leading-none tabular-nums",
                coverage >= threshold ? "text-rose-600 dark:text-rose-400" : "text-emerald-700 dark:text-emerald-400",
              )}
            >
              {pct(shown)}
              <span className="ml-1 text-base font-medium">%</span>
            </div>
            <LimitBar
              value={coverage}
              limit={threshold}
              scale={scale}
              color={coverage >= threshold ? "#e11d48" : "#059669"}
              className="mt-3"
            />
          </div>
        ) : (
          <div className="mt-3 text-xs text-muted-foreground">
            {t("obstacle.skipped", { reason: segmentation?.skipped ?? "—" })}
          </div>
        )}

        {segmentation && !segmentation.enforced && !segmentation.skipped && (
          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">{t("obstacle.shadow")}</div>
        )}

        <div className="mt-auto flex items-start gap-1.5 pt-3 text-[11px] text-muted-foreground/80">
          {preview && (
            <>
              <Info className="mt-px h-3 w-3 shrink-0" />
              <span className="flex-1">{t("obstacle.preview")}</span>
            </>
          )}
          {segmentation?.version && (
            <span className="ml-auto whitespace-nowrap tabular-nums">
              {t("obstacle.model", { version: segmentation.version })}
            </span>
          )}
        </div>
      </div>

      <div className="min-w-0 space-y-1.5">
        {classes.map((c) => (
          <ObstacleRow
            key={c.name}
            obstacle={c}
            label={classLabel(c.name)}
            threshold={threshold}
            scale={scale}
            hovered={hovered}
            onHover={onHover}
          />
        ))}
        {coverage !== null && classes.length === 0 && (
          <div className="flex h-10 items-center gap-2 rounded-lg border border-dashed px-3 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            {t("obstacle.nothingFound")}
          </div>
        )}
        {showGap && (
          <div
            className={cn(
              "cursor-pointer rounded-lg border px-3 py-2 transition-[background-color,box-shadow,opacity] duration-200",
              hovered === OBSTACLE_GAP_KEY && "bg-muted shadow-sm",
              hovered !== null && hovered !== OBSTACLE_GAP_KEY && "opacity-50",
            )}
            {...highlight(OBSTACLE_GAP_KEY)}
          >
            <div className="flex items-center gap-2 text-sm">
              <Swatch color={OBSTACLE_GAP_COLOR} hatched={gapFailed} />
              <span className="min-w-0 truncate font-medium">{t("obstacle.gap")}</span>
              <span
                className={cn(
                  "ml-auto whitespace-nowrap font-semibold tabular-nums",
                  gapFailed && "text-amber-600 dark:text-amber-400",
                )}
              >
                {t("obstacle.gapValue", { value: pct(gapRatio) })}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <LimitBar
                value={gapRatio}
                limit={gapThreshold}
                scale={Math.max(gapThreshold * 2, gapRatio * 1.15, 0.1)}
                color={OBSTACLE_GAP_COLOR}
                className="h-1.5 flex-1"
              />
              <span className="whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
                {t("obstacle.limit", { value: pct(gapThreshold, 0) })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ObstacleRow({
  obstacle,
  label,
  threshold,
  scale,
  hovered,
  onHover,
}: {
  obstacle: ObstacleClass;
  label: string;
  threshold: number;
  scale: number;
  hovered: string | null;
  onHover: (key: string | null) => void;
}) {
  const { t, formatNumber } = useLocalization();
  const highlight = useHighlight(hovered, onHover);
  const covers = obstacle.coverage > 0;
  const color = covers ? obstacleColor(obstacle.name) : OBSTACLE_MUTED_COLOR;
  const Icon = CLASS_ICONS[obstacle.name] ?? Layers;
  const isHovered = hovered === obstacle.name;
  const pct = (v: number, digits = 1) =>
    formatNumber(v * 100, { maximumFractionDigits: digits, minimumFractionDigits: digits });
  const confidence = (
    <span className="whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
      {t("obstacle.confidence", { value: pct(obstacle.score, 0) })}
    </span>
  );
  return (
    <div
      className={cn(
        "cursor-pointer rounded-lg border px-3 py-2 transition-[background-color,box-shadow,opacity] duration-200",
        isHovered && "bg-muted shadow-sm",
        hovered !== null && !isHovered && "opacity-50",
      )}
      style={isHovered ? { boxShadow: `inset 3px 0 0 ${color}` } : undefined}
      {...highlight(obstacle.name)}
    >
      <div className="flex items-center gap-2 text-sm">
        <Swatch color={color} hatched={covers} />
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate font-medium">{label}</span>
        {!covers && confidence}
        <span className="ml-auto whitespace-nowrap font-semibold tabular-nums">
          {covers ? t("obstacle.ofPizza", { value: pct(obstacle.coverage) }) : t("obstacle.outside")}
        </span>
      </div>
      {covers && (
        <div className="mt-2 flex items-center gap-3">
          <LimitBar value={obstacle.coverage} limit={threshold} scale={scale} color={color} className="h-1.5 flex-1" />
          {confidence}
        </div>
      )}
    </div>
  );
}

/** Mask / photo switch over the photo (keyboard: M). */
export function ObstacleModeToggle({
  mode,
  onChange,
  className,
}: {
  mode: ObstacleViewMode;
  onChange: (mode: ObstacleViewMode) => void;
  className?: string;
}) {
  const { t } = useLocalization();
  const options: { value: ObstacleViewMode; label: string; icon: React.ReactNode }[] = [
    { value: "mask", label: t("obstacle.modeMask"), icon: <Layers className="h-3.5 w-3.5" /> },
    { value: "source", label: t("obstacle.modeSource"), icon: <ImageIcon className="h-3.5 w-3.5" /> },
  ];
  return (
    <div
      className={cn(
        "obstacle-legend pointer-events-auto absolute bottom-3 right-3 z-30 flex items-center gap-1 rounded-full",
        "border border-white/15 bg-slate-950/70 p-1 text-xs text-white shadow-xl backdrop-blur-md",
        className,
      )}
    >
      <div className="relative flex">
        <span
          className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-white shadow transition-transform duration-300 ease-out"
          style={{ transform: mode === "source" ? "translateX(100%)" : "none" }}
        />
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "relative z-10 flex w-9 items-center justify-center gap-1.5 rounded-full px-2.5 py-1 font-medium transition-colors duration-300 sm:w-[5.6rem]",
              mode === o.value ? "text-slate-900" : "text-white/75 hover:text-white",
            )}
            aria-pressed={mode === o.value}
            aria-label={o.label}
          >
            {o.icon}
            <span className="hidden sm:inline">{o.label}</span>
          </button>
        ))}
      </div>
      <kbd className="mx-1 hidden rounded border border-white/20 bg-white/10 px-1.5 text-[10px] font-semibold text-white/70 sm:block">
        M
      </kbd>
    </div>
  );
}
